import { Flex, Box, Button, Icon } from '@chakra-ui/react';
import { useFormik, FormikProvider, FieldArray, Form } from 'formik';
import * as Yup from 'yup';
import { PlusIcon } from '@heroicons/react/solid';
import type { FilterProps } from '../';
import { FilterRow } from './filterItem';
import { getDefaultFilterCondition } from './filterItemConstants';
import { useMemo } from 'react';
import { FILTER_CONFIG } from './filter-config/use-filter-config';
import { FilterType } from './filter-config/types';
import { KText } from '@/ui-library/components/text';

const validationSchema = Yup.object().shape({
  filters: Yup.array().of(
    Yup.object().shape({
      filter_by: Yup.object().required('Filter term is required'),
      filter_condition: Yup.object().required('Filter condition is required'),
      filter_by_values: Yup.array().required('Filter value is required'),
    })
  ),
});

export const FilterContent = ({
  filtersToShow = Object.keys(FILTER_CONFIG).filter(
    (key) => key in FILTER_CONFIG
  ) as FilterType[], // Set default to show all filters if undefined,
  selected,
  onSave,
  onReset,
  isEditMode: _isEditMode = false,
  reportType,
  onClose,
}: Pick<
  FilterProps,
  | 'filtersToShow'
  | 'selected'
  | 'onSave'
  | 'onReset'
  | 'isEditMode'
  | 'reportType'
  | 'onClose'
>) => {
  const initialValues = useMemo(() => {
    if (selected?.length === 0) {
      return [
        {
          filter_by: {
            value: filtersToShow[0],
            label: FILTER_CONFIG[filtersToShow[0]].name,
          },
          filter_condition: getDefaultFilterCondition(filtersToShow[0]),
          filter_by_values: [],
        },
      ];
    }
    return selected;
  }, [filtersToShow, selected]);

  const formik = useFormik({
    initialValues: { filters: initialValues },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      try {
        onSave(values.filters ?? []);
      } catch {
        // Form save handled by onSave
      }
    },
  });

  const nextFilters = useMemo(() => {
    const nextFilterToShow = filtersToShow.filter((filter) => {
      return formik?.values?.filters?.every(
        (f) => f?.filter_by?.value !== filter
      );
    });
    return nextFilterToShow;
  }, [formik, filtersToShow]);

  const addFilter = ({ arrayHelpers = {} }) => {
    arrayHelpers?.push({
      filter_by: {
        value: nextFilters[0],
        label: FILTER_CONFIG[nextFilters[0]].name,
      },
      filter_condition: getDefaultFilterCondition(nextFilters[0]),
      filter_by_values: [],
    });
  };

  return (
    <Box p="12px 20px">
      <KText size="sm-semi-bold" color="secondary-900">
        In this view filter
      </KText>
      <FormikProvider value={formik}>
        <Form noValidate>
          <FieldArray
            name="filters"
            render={(arrayHelpers) => {
              return (
                <>
                  <Box maxH="450px" overflowY="auto">
                    {formik.values?.filters?.map((filter, idx) => {
                      return (
                        <FilterRow
                          key={idx}
                          idx={idx}
                          arrayHelpers={arrayHelpers}
                          filter={filter}
                          options={nextFilters}
                          reportType={reportType}
                        />
                      );
                    })}
                  </Box>
                  <Flex
                    borderTop="1px solid #EAECF0"
                    mt="12px"
                    pt="12px"
                    alignItems="center"
                  >
                    {nextFilters?.length > 0 && (
                      <Flex alignItems="center" cursor="pointer">
                        <Icon
                          as={PlusIcon}
                          width="12px"
                          height="12px"
                          color="primary-500"
                        />
                        <KText
                          size="sm-bold"
                          color="primary-500"
                          onClick={() => addFilter({ arrayHelpers })}
                        >
                          Add Filter
                        </KText>
                      </Flex>
                    )}
                    <Box flex="1" textAlign="right">
                      <Button
                        variant="secondary"
                        onClick={() => {
                          formik?.resetForm();
                          onReset();
                          onClose();
                        }}
                      >
                        Clear all
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        ml="12px"
                        isDisabled={!formik?.dirty}
                      >
                        Apply
                      </Button>
                    </Box>
                  </Flex>
                </>
              );
            }}
          />
        </Form>
      </FormikProvider>
    </Box>
  );
};
